import { Box, Container, VStack } from '@chakra-ui/react'
import { revalidateLogic } from '@tanstack/react-form'
import { Footer } from 'commons/components/footer/Footer'
import { HeaderSection } from 'commons/components/header/index'
import { LanguageSwitch } from 'commons/components/layout/LanguageSwitch'
import { PageErrorCentered } from 'commons/components/layout/PageErrorCentered'
import PageLayout from 'commons/components/layout/PageLayout'
import { PageLoaderCentered } from 'commons/components/layout/PageLoaderCentered'
import { ShareButton } from 'commons/components/layout/ShareButton'
import { PaymentSubmit } from 'commons/components/PaymentSubmit'
import { UnifiedPaymentModal } from 'commons/components/modal/UnifiedPaymentModal'
import { IbanField } from 'commons/components/moneyPot/IbanField'
import QRCodeApp from 'commons/components/phone/QRCode'
import { useAppForm } from 'commons/config/form'
import { createSchema, defaultLydiaPageValues } from 'commons/config/value'
import { PaymentSources, PaymentTypes, isXpayType } from 'commons/constants/paymentTypes'
import { useFeatureFlipping } from 'commons/hooks/services/useFeatureFlipping'
import useLydiaPage from 'commons/hooks/services/useLydiaPage'
import { postLydiaPageRequest } from 'commons/services/api/paymentApi'
import type { PaymentSubmitRequest } from 'commons/types/paymentTypes'
import { toNumber } from 'commons/utils/amountHelpers'
import { isHideLydiaFromUrl } from 'commons/utils/hideLydiaHelpers'
import { getPendingPaymentUrl } from 'commons/utils/urlUtils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const LydiaPage = () => {
	const [isUnifiedModalOpen, setIsUnifiedModalOpen] = useState(false)
	const [unifiedModalData, setUnifiedModalData] = useState<PaymentSubmitRequest | null>(null)
	const handlePaymentReset = useCallback(() => {
		setIsUnifiedModalOpen(false)
		setUnifiedModalData(null)
	}, [])
	const { data: featureFlippings } = useFeatureFlipping()

	const pageTag = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search)
		return urlParams.get('t') || ''
	}, [])
	const { data: lydiaPageData, isLoading, isError } = useLydiaPage(pageTag)
	const { t, i18n } = useTranslation()
	const hideLydia = useMemo(() => isHideLydiaFromUrl(), [])

	// SA-2682: Parse URL parameters for pre-filling form fields
	// ex: https://preprod.pay.lydia.me/l?t=ericg4810&a=2500&d=anniversaire&e=emile@mail.com
	const initialFormValues = useMemo(() => {
		const urlParams = new URLSearchParams(window.location.search)
		const values = { ...defaultLydiaPageValues }
		const amountParam = urlParams.get('a')
		if (amountParam) {
			const amountInCents = parseInt(amountParam, 10)
			if (!isNaN(amountInCents)) {
				values.amount = (amountInCents / 100).toString()
			}
		}
		const reasonParam = urlParams.get('d')
		if (reasonParam) {
			values.motif = decodeURIComponent(reasonParam)
		}
		const emailParam = urlParams.get('e')
		if (emailParam) {
			values.email = decodeURIComponent(emailParam)
		}
		if (hideLydia) {
			values.paymentType = PaymentTypes.CARD
		}
		return values
	}, [hideLydia])

	const form = useAppForm({
		defaultValues: initialFormValues,
		validationLogic: revalidateLogic({
			mode: 'blur',
			modeAfterSubmission: 'change'
		}),
		validators: {
			onDynamic: ({ formApi }) =>
				formApi.parseValuesWithSchema(
					createSchema(
						() => formApi.getFieldValue('amount'),
						() => formApi.getFieldValue('paymentType'),
						undefined,
						() => formApi.getFieldValue('paymentType') !== PaymentTypes.TRANSFER
					)
				)
		},
		onSubmit: async ({ value, formApi }) => {
			// Discard the previous wallet session before creating a request with
			// the latest amount, payment method, and payer details.
			setIsUnifiedModalOpen(false)
			setUnifiedModalData(null)
			try {
				const amount = toNumber(value.amount) ?? 0
				await postLydiaPageRequest(
					amount,
					value.paymentType,
					value.motif,
					value.phone || '',
					value.email || '',
					pageTag,
					(requestPubUuid: string, amount: number) => {
						setUnifiedModalData({
							requestPubUuid,
							amount,
							paymentType: value.paymentType,
							formValues: JSON.stringify(value)
						})
						setIsUnifiedModalOpen(true)
					}
				)
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'An error occurred'
				// Set error on the appropriate field based on payment type
				const errorObject = new Error(errorMessage)
				if (value.paymentType === PaymentTypes.LYDIA) {
					formApi.setFieldMeta('phone', (prev) => ({
						...prev,
						errors: [errorObject],
						isTouched: true,
						isValid: false,
						errorMap: { onSubmit: errorMessage }
					}))
				} else {
					formApi.setFieldMeta('email', (prev) => ({
						...prev,
						errors: [errorObject],
						isTouched: true,
						isValid: false,
						errorMap: { onSubmit: errorMessage }
					}))
				}
				// Rethrow to prevent form from thinking submission succeeded
				throw error
			}
		},
		onSubmitInvalid: ({ formApi }) => {
			// Force touch all fields to show errors
			const fieldNames = ['amount', 'paymentType', 'motif', 'phone', 'email'] as const
			fieldNames.forEach((fieldName) => {
				formApi.setFieldMeta(fieldName, (prev) => ({ ...prev, isTouched: true }))
			})
		}
	})

	// Track previous payment type to clear fields only when it changes
	const prevPaymentTypeRef = useRef(defaultLydiaPageValues.paymentType)

	useEffect(() => {
		const unsubscribe = form.store.subscribe(() => {
			const currentPaymentType = form.getFieldValue('paymentType')
			const prevPaymentType = prevPaymentTypeRef.current
			if (currentPaymentType !== prevPaymentType) {
				if (currentPaymentType === PaymentTypes.LYDIA) {
					// Clear email when switching to Lydia
					const emailValue = form.getFieldValue('email')
					if (emailValue) {
						form.setFieldValue('email', '')
					}
				} else if (currentPaymentType === PaymentTypes.CARD || isXpayType(currentPaymentType)) {
					// Clear phone when switching to Card
					const phoneValue = form.getFieldValue('phone')
					if (phoneValue) {
						form.setFieldValue('phone', '')
					}
				}
				prevPaymentTypeRef.current = currentPaymentType
			}
		})
		return unsubscribe
	}, [form])

	const handlePaymentCompleted = useCallback(() => {
		if (!unifiedModalData) return
		window.location.href = getPendingPaymentUrl(
			process.env.REACT_APP_POTS_URL,
			unifiedModalData.requestPubUuid,
			i18n.language
		)
	}, [unifiedModalData, i18n])

	if (isLoading) {
		return <PageLoaderCentered />
	}

	if (isError) {
		return <PageErrorCentered message={t('phone_form.error_fetching_data')} />
	}

	return (
		<PageLayout>
			<Box position='absolute' top={'40px'} right={{ base: '26px', md: '60px' }} zIndex={1}>
				<ShareButton />
			</Box>
			<Container centerContent maxW='600px' mt={{ base: '80px', md: '0' }} p={4}>
				<HeaderSection userInfo={lydiaPageData} />
				<VStack
					gap={0.5}
					w='full'
					mt={10}
					as='form'
					onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
						e.preventDefault()
						e.stopPropagation()
					}}
				>
					<form.AppForm>
						{form.AppField({ name: 'amount', children: (field) => <field.AmountSection /> })}
						{form.AppField({
							name: 'paymentType',
							children: (field) => (
								<field.PaymentTypeSection
									featureFlippings={featureFlippings}
									collectIban={lydiaPageData?.collectIban}
									hideLydia={hideLydia}
								/>
							)
						})}
						{form.Subscribe({
							selector: (state) => state.values.paymentType,
							children: (paymentType) =>
								paymentType !== PaymentTypes.TRANSFER &&
								form.AppField({
									name: 'motif',
									children: (field) => <field.MotifSection />
								})
						})}
						{form.Subscribe({
							selector: (state) => ({
								paymentType: state.values.paymentType,
								amount: state.values.amount,
								motif: state.values.motif
							}),
							children: ({ paymentType, amount, motif }) => (
								<>
									{paymentType === PaymentTypes.LYDIA && (
										<QRCodeApp
											context='payment'
											identifier={pageTag}
											amount={amount}
											description={motif}
											lydiaLogoUrl={`${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/logo_app_lydia.png`}
											sumeriaLogoUrl={`${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/icons/lydiaSumeriaSmall.png`}
										/>
									)}
								</>
							)
						})}
						{form.Subscribe({
							selector: (state) => ({
								paymentType: state.values.paymentType,
								amount: state.values.amount
							}),
							children: ({ paymentType, amount }) => (
								<>
									{paymentType === PaymentTypes.LYDIA &&
										form.AppField({
											name: 'phone',
											children: (field) => <field.PhoneSection />
										})}
									{(paymentType === PaymentTypes.CARD || isXpayType(paymentType)) &&
										form.AppField({ name: 'email', children: (field) => <field.EmailSection /> })}
									{paymentType === PaymentTypes.TRANSFER && lydiaPageData?.collectIban && (
										<IbanField
											iban={lydiaPageData.collectIban}
											name={lydiaPageData.collectorName}
											bic={lydiaPageData.collectBic}
											identifier={pageTag}
											amount={toNumber(amount) ?? 0}
											source={PaymentSources.LYDIA_PAGE}
										/>
									)}
								</>
							)
						})}
						{form.Subscribe({
							selector: (state) => state.values.paymentType,
							children: (paymentType) =>
								(unifiedModalData && isXpayType(unifiedModalData.paymentType)) ||
								paymentType !== PaymentTypes.TRANSFER ? (
									<PaymentSubmit
										request={isUnifiedModalOpen ? unifiedModalData : null}
										onReset={handlePaymentReset}
										onPaymentCompleted={handlePaymentCompleted}
									/>
								) : null
						})}
					</form.AppForm>
					<Footer firstname={lydiaPageData?.name} />
					<LanguageSwitch />
				</VStack>
			</Container>
			{unifiedModalData && !isXpayType(unifiedModalData.paymentType) && (
				<UnifiedPaymentModal
					key={unifiedModalData.requestPubUuid}
					isOpen={isUnifiedModalOpen}
					onClose={handlePaymentReset}
					requestPubUuid={unifiedModalData.requestPubUuid}
					onPaymentCompleted={handlePaymentCompleted}
					source='lydia_page'
					paymentType={unifiedModalData.paymentType}
					amount={unifiedModalData.amount}
				/>
			)}
		</PageLayout>
	)
}

export default LydiaPage
